import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Heart, BadgeCheck, Users2, ShieldCheck, GraduationCap, Award, Microscope, Stethoscope } from "lucide-react";
import { DR_KAMLESH_IMG, DR_DHARMIN_IMG } from "@/lib/assets";
import useSEO from "@/hooks/useSEO";
import { api } from "@/lib/api";
import { ssgData } from "@/lib/ssg";
import { useLang } from "@/context/LangContext";

const ABOUT_IMG = "https://images.unsplash.com/photo-1771574204208-b47e2d863bc5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwyfHxjYWxtJTIwbW9kZXJuJTIwaG9zcGl0YWwlMjBpbnRlcmlvciUyMGNsaW5pY3xlbnwwfHx8fDE3Nzg3NDM1MDV8MA&ixlib=rb-4.1.0&q=85";

const values = [
  { icon: Heart, title: "Empathy first", desc: "Care is not just clinical — it is human. We listen before we prescribe." },
  { icon: BadgeCheck, title: "Evidence-based", desc: "Every protocol is built on peer-reviewed psychiatric science." },
  { icon: Users2, title: "Family-centred", desc: "We treat patients in the context of the people who love them." },
  { icon: ShieldCheck, title: "Confidentiality", desc: "Discretion is built into our walls. Your story stays yours." },
];

export default function About() {
  useSEO({
    title: "About — Dr. Kamlesh A. Shah & Dr. Dharmin K. Shah",
    description: "Manas Neuropsychiatry Hospital, Bhavnagar — established 1989. Meet our doctors: Dr. Kamlesh A. Shah (Founder, MD Neuropsychiatry) and Dr. Dharmin K. Shah (TMS Physician, AIIMS Delhi). Press features, awards and combined clinical practice.",
    image: DR_DHARMIN_IMG,
  });
  const [press, setPress] = useState(() => ssgData("press") || []);
  const { t } = useLang();
  useEffect(() => {
    api.get("/press").then(({ data }) => setPress(data)).catch(() => {});
  }, []);
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-16 md:pb-20">
        <p className="eyebrow text-[#A39171] mb-6">{t("About Manas")}</p>
        <h1 className="font-display text-5xl md:text-7xl text-[#2D2623] leading-[0.95] tracking-tight max-w-4xl">
          {t("A quiet practice for")} <span className="italic text-[#E07A5F]">{t("loud feelings")}</span>.
        </h1>
        <p className="mt-8 text-xl text-[#5C524E] max-w-2xl leading-relaxed">
          {t("Manas Neuropsychiatry Hospital was founded with one belief: serious mental healthcare can be both clinically rigorous and warmly human. We are a multi-disciplinary practice in Bhavnagar focused on long-term outcomes, not quick fixes.")}
        </p>
      </section>

      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="rounded-3xl overflow-hidden aspect-[16/9] md:aspect-[21/9]">
            <img src={ABOUT_IMG} alt="Calm hospital interior" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <p className="eyebrow text-[#A39171] mb-4">{t("Our Mission")}</p>
            <h2 className="font-display text-4xl md:text-5xl text-[#2D2623] tracking-tight leading-tight">
              {t("To return dignity to mental healthcare.")}
            </h2>
          </div>
          <div className="md:col-span-7 text-[#5C524E] text-lg leading-relaxed space-y-5">
            <p>
              {t("We started Manas because we kept meeting brilliant patients who had been failed by a system that treated symptoms instead of people. We wanted to build a place where the waiting room felt like a friend's living room, where therapists had time, and where neuromodulation was not a luxury but a standard.")}
            </p>
            <p>
              {t("Today, our clinical board includes psychiatrists, psychologists, addiction medicine specialists, and sex-therapy consultants. Every patient is reviewed by more than one clinician. Every plan is documented, measured and adapted.")}
            </p>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="bg-[#F4EFE6]/60 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <p className="eyebrow text-[#A39171] mb-4">{t("What we stand for")}</p>
          <h2 className="font-display text-4xl md:text-5xl text-[#2D2623] tracking-tight mb-14 max-w-3xl">
            {t("Four values,")} <span className="italic text-[#E07A5F]">{t("non-negotiable.")}</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {values.map((v, i) => (
              <div
                key={v.title}
                data-testid={`value-${i}`}
                className="bg-[#FDFBF7] rounded-3xl p-8 md:p-10 border border-[#A39171]/15 hover:border-[#E07A5F]/40 transition-colors"
              >
                <div className="flex items-start gap-5">
                  <span className="w-12 h-12 rounded-2xl bg-[#2D2623] text-[#FDFBF7] flex items-center justify-center shrink-0">
                    <v.icon className="w-5 h-5" />
                  </span>
                  <div>
                    <h3 className="font-display text-2xl text-[#2D2623] tracking-tight">{t(v.title)}</h3>
                    <p className="text-[#5C524E] mt-2">{t(v.desc)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MEET YOUR DOCTORS */}
      <section id="doctors" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-3xl mb-14">
            <p className="eyebrow text-[#A39171] mb-4">{t("Meet Your Doctors")}</p>
            <h2 className="font-display text-4xl md:text-6xl text-[#2D2623] tracking-tight leading-[1.05]">
              {t("Two generations of")} <span className="italic text-[#E07A5F]">{t("psychiatric expertise")}</span>.
            </h2>
            <p className="mt-6 text-lg text-[#5C524E] leading-relaxed">
              {t("Father–son, neuropsychiatrist and interventional neuropsychiatrist — together they bring more than three decades of unbroken clinical practice to Bhavnagar.")}
            </p>
          </div>

          {/* Two profile cards with individual photos */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Dr. Kamlesh */}
            <article
              data-testid="doctor-kamlesh"
              className="bg-[#F4EFE6] rounded-3xl overflow-hidden border border-[#A39171]/15 hover:shadow-[0_30px_70px_rgba(163,145,113,0.18)] transition-shadow group"
            >
              <div className="aspect-[4/5] overflow-hidden bg-gradient-to-br from-[#2D2623] via-[#5C524E] to-[#2D2623] relative">
                <img
                  src={DR_KAMLESH_IMG}
                  alt="Dr. Kamlesh A. Shah"
                  className="w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-700"
                />
                <div className="absolute top-5 left-5 bg-[#FDFBF7]/95 backdrop-blur-md rounded-full px-4 py-1.5 text-[10px] uppercase tracking-[0.18em] text-[#2D2623] font-medium">
                  {t("Founder")}
                </div>
              </div>
              <div className="p-8 md:p-10">
                <p className="eyebrow text-[#E07A5F] mb-3">{t("Senior Consultant")}</p>
                <h3 className="font-display text-3xl md:text-4xl text-[#2D2623] tracking-tight">
                  Dr. Kamlesh A. Shah
                </h3>
                <p className="text-[#5C524E] mt-1 text-sm">MD Neuropsychiatry · MBBS · FIPS · FIAPP</p>

                <div className="mt-7 space-y-5">
                  <div className="flex items-start gap-4">
                    <span className="w-10 h-10 rounded-2xl bg-[#2D2623] text-[#FDFBF7] flex items-center justify-center shrink-0">
                      <Stethoscope className="w-4 h-4" />
                    </span>
                    <div>
                      <p className="text-xs uppercase tracking-[0.16em] text-[#A39171]">{t("Speciality")}</p>
                      <p className="text-[#2D2623] mt-1">Neuropsychiatry · Deaddiction Specialist · Sexologist</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <span className="w-10 h-10 rounded-2xl bg-[#2D2623] text-[#FDFBF7] flex items-center justify-center shrink-0">
                      <GraduationCap className="w-4 h-4" />
                    </span>
                    <div>
                      <p className="text-xs uppercase tracking-[0.16em] text-[#A39171]">{t("Education")}</p>
                      <p className="text-[#2D2623] mt-1">MBBS &amp; MD — B. J. Medical College, Ahmedabad</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <span className="w-10 h-10 rounded-2xl bg-[#2D2623] text-[#FDFBF7] flex items-center justify-center shrink-0">
                      <Award className="w-4 h-4" />
                    </span>
                    <div>
                      <p className="text-xs uppercase tracking-[0.16em] text-[#A39171]">{t("Affiliations")}</p>
                      <p className="text-[#2D2623] mt-1">Life Fellow, Indian Psychiatric Society</p>
                    </div>
                  </div>
                </div>

                <Link
                  to="/doctors/dr-kamlesh-shah"
                  data-testid="about-dr-kamlesh-cta"
                  className="mt-8 inline-flex items-center gap-2 text-[#2D2623] hover:text-[#E07A5F] font-medium link-underline"
                >
                  {t("Full profile")} <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </article>

            {/* Dr. Dharmin */}
            <article
              data-testid="doctor-dharmin"
              className="bg-[#F4EFE6] rounded-3xl overflow-hidden border border-[#A39171]/15 hover:shadow-[0_30px_70px_rgba(224,122,95,0.18)] transition-shadow group"
            >
              <div className="aspect-[4/5] overflow-hidden bg-gradient-to-br from-[#2D2623] via-[#5C524E] to-[#2D2623] relative">
                <img
                  src={DR_DHARMIN_IMG}
                  alt="Dr. Dharmin K. Shah"
                  className="w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-700"
                />
                <div className="absolute top-5 left-5 bg-[#E07A5F] text-white rounded-full px-4 py-1.5 text-[10px] uppercase tracking-[0.18em] font-medium">
                  TMS — AIIMS Delhi
                </div>
              </div>
              <div className="p-8 md:p-10">
                <p className="eyebrow text-[#E07A5F] mb-3">{t("Interventional Neuropsychiatrist")}</p>
                <h3 className="font-display text-3xl md:text-4xl text-[#2D2623] tracking-tight">
                  Dr. Dharmin K. Shah
                </h3>
                <p className="text-[#5C524E] mt-1 text-sm">MD Neuropsychiatry · TMS Physician (AIIMS, Delhi) · MBBS · MIPS</p>

                <div className="mt-7 space-y-5">
                  <div className="flex items-start gap-4">
                    <span className="w-10 h-10 rounded-2xl bg-[#2D2623] text-[#FDFBF7] flex items-center justify-center shrink-0">
                      <Stethoscope className="w-4 h-4" />
                    </span>
                    <div>
                      <p className="text-xs uppercase tracking-[0.16em] text-[#A39171]">{t("Speciality")}</p>
                      <p className="text-[#2D2623] mt-1">Interventional Neuropsychiatry · Deaddiction · Sexology</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <span className="w-10 h-10 rounded-2xl bg-[#2D2623] text-[#FDFBF7] flex items-center justify-center shrink-0">
                      <GraduationCap className="w-4 h-4" />
                    </span>
                    <div>
                      <p className="text-xs uppercase tracking-[0.16em] text-[#A39171]">{t("Education")}</p>
                      <p className="text-[#2D2623] mt-1 leading-relaxed">
                        MBBS — GMC Bhavnagar<br />
                        MD — SBKS Medical Institute, Vadodara<br />
                        TMS Physician — AIIMS, New Delhi
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <span className="w-10 h-10 rounded-2xl bg-[#2D2623] text-[#FDFBF7] flex items-center justify-center shrink-0">
                      <Microscope className="w-4 h-4" />
                    </span>
                    <div>
                      <p className="text-xs uppercase tracking-[0.16em] text-[#A39171]">{t("Publications")}</p>
                      <p className="text-[#2D2623] mt-1">Annals of Indian Psychiatry · Journal of Psychosexual Health</p>
                    </div>
                  </div>
                </div>

                <Link
                  to="/doctors/dr-dharmin-shah"
                  data-testid="about-dr-dharmin-cta"
                  className="mt-8 inline-flex items-center gap-2 text-[#2D2623] hover:text-[#E07A5F] font-medium link-underline"
                >
                  {t("Full profile")} <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* COMBINED PRACTICE — what both doctors treat */}
      <section className="bg-[#F4EFE6]/60 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-3xl mb-14">
            <p className="eyebrow text-[#A39171] mb-4">{t("Combined Practice")}</p>
            <h2 className="font-display text-4xl md:text-5xl text-[#2D2623] tracking-tight leading-tight">
              {t("A full-spectrum psychiatric &")} <span className="italic text-[#E07A5F]">{t("neuropsychiatric")}</span> {t("practice.")}
            </h2>
            <p className="mt-6 text-lg text-[#5C524E] leading-relaxed">
              {t("Both Drs. Shah have extensive experience in the assessment, diagnosis and treatment of psychiatric disorders across every age group. Their practice covers the full clinical spectrum below.")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {/* Adult Psychiatry */}
            <div data-testid="practice-adult" className="bg-[#FDFBF7] rounded-3xl p-8 border border-[#A39171]/15">
              <p className="eyebrow text-[#E07A5F] mb-4">{t("Adult Psychiatry")}</p>
              <ul className="space-y-2.5 text-[#2D2623] text-[15px]">
                {[
                  "Depression",
                  "Bipolar Mood Disorder",
                  "Generalised Anxiety Disorder",
                  "Acute Stress Disorder",
                  "Schizophrenia · Schizoaffective Disorder",
                  "Delusional Disorder",
                  "Obsessive Compulsive Disorder (OCD)",
                  "Panic Disorder",
                  "Somatoform &amp; Pain Disorders",
                  "Tension Headache &amp; other headaches",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#E07A5F] mt-2 shrink-0" />
                    <span dangerouslySetInnerHTML={{ __html: t }} />
                  </li>
                ))}
              </ul>
            </div>

            {/* Neuropsychiatry & Sexology */}
            <div data-testid="practice-neuro" className="bg-[#FDFBF7] rounded-3xl p-8 border border-[#A39171]/15">
              <p className="eyebrow text-[#E07A5F] mb-4">{t("Neuropsychiatry · Sex Therapy")}</p>
              <ul className="space-y-2.5 text-[#2D2623] text-[15px]">
                {[
                  "Dementia (Alzheimer's &amp; Vascular)",
                  "Migraine &amp; chronic headaches",
                  "Behavioural &amp; cognitive disturbances after brain injury",
                  "Erectile Dysfunction",
                  "Premature Ejaculation",
                  "Hypoactive Sexual Desire Disorder",
                  "Sexual Pain Disorder",
                  "Sexual Addiction",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#E07A5F] mt-2 shrink-0" />
                    <span dangerouslySetInnerHTML={{ __html: t }} />
                  </li>
                ))}
              </ul>
            </div>

            {/* Child & Adolescent */}
            <div data-testid="practice-child" className="bg-[#FDFBF7] rounded-3xl p-8 border border-[#A39171]/15">
              <p className="eyebrow text-[#E07A5F] mb-4">{t("Child & Adolescent Psychiatry")}</p>
              <ul className="space-y-2.5 text-[#2D2623] text-[15px]">
                {[
                  "Attention Deficit Hyperkinetic Disorder (ADHD)",
                  "Autism Spectrum Disorder",
                  "Temper tantrums",
                  "Intellectual disability",
                  "Behavioural disturbances",
                  "Enuresis · Encopresis",
                  "Oppositional Defiant Disorder",
                  "Disruptive Mood Dysregulation Disorder",
                  "Childhood mood &amp; anxiety disorders",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#E07A5F] mt-2 shrink-0" />
                    <span dangerouslySetInnerHTML={{ __html: t }} />
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Vyasan Mukt Campus highlight */}
          <div className="mt-12 bg-[#2D2623] text-[#FDFBF7] rounded-3xl p-8 md:p-12 grid grid-cols-1 md:grid-cols-12 gap-8 items-center relative overflow-hidden">
            <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
            <div className="md:col-span-8 relative">
              <p className="eyebrow text-[#E2C044] mb-4">{t("Special Interest — Drug Detoxification")}</p>
              <h3 className="font-display text-3xl md:text-4xl tracking-tight leading-tight">
                {t("The")} <span className="italic text-[#E07A5F]">"Vyasan Mukt Campus"</span> {t("movement.")}
              </h3>
              <p className="mt-5 text-[#FDFBF7]/80 leading-relaxed">
                {t("Dr. Dharmin Shah founded Vyasan Mukt Campus — a sustained awareness campaign across Bhavnagar district to make youth addiction-free. Over 50 awareness campaigns, dozens of lectures in schools, colleges and institutes, and training programmes for healthcare workers have been delivered under this banner.")}
              </p>
              <p className="mt-4 text-[#FDFBF7]/70 leading-relaxed text-sm">
                {t("Both doctors have treated patients across the addiction spectrum — alcohol, tobacco, ganja, charas, bidi, afin, opium, brown sugar, alprazolam, pethidine, cocaine and marijuana — and regularly appear on TV channels, newspapers and magazines as expert voices on mental health.")}
              </p>
            </div>
            <div className="md:col-span-4 relative">
              <div className="bg-[#FDFBF7]/5 border border-[#FDFBF7]/15 rounded-2xl p-6">
                <p className="font-display text-5xl text-[#E2C044]">50+</p>
                <p className="text-sm text-[#FDFBF7]/70 mt-2">{t("awareness campaigns & lectures across Bhavnagar district")}</p>
              </div>
            </div>
          </div>

          <p className="mt-12 text-[#5C524E] text-base md:text-lg leading-relaxed max-w-4xl">
            {t("Both doctors are skilled in therapeutic communication — the quiet art of establishing rapport with mentally-ill patients — and continuously update their clinical knowledge by attending national and international conferences.")}
          </p>
        </div>
      </section>

      {/* PRESS / MEDIA WALL */}
      <section id="press" className="py-24 md:py-32 bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <p className="eyebrow text-[#A39171] mb-4">{t("Press & Media")}</p>
              <h2 className="font-display text-4xl md:text-5xl text-[#2D2623] tracking-tight leading-tight">
                {t("As featured in")} <span className="italic text-[#E07A5F]">{t("leading publications")}</span>.
              </h2>
              <p className="mt-6 text-lg text-[#5C524E] leading-relaxed">
                {t("Our doctors regularly write, speak and consult for India's most-read regional and national media on mental health, deaddiction and neuromodulation.")}
              </p>
            </div>
            <div className="flex items-center gap-3 text-sm text-[#5C524E]">
              <span className="px-3 py-1.5 rounded-full bg-[#F4EFE6] border border-[#A39171]/20">Divya Bhaskar</span>
              <span className="px-3 py-1.5 rounded-full bg-[#F4EFE6] border border-[#A39171]/20">Pride of Bhavnagar 2026</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
            {press.map((p) => {
              const span =
                p.size === "lg" ? "md:col-span-7" :
                p.size === "md" ? "md:col-span-5" :
                p.size === "wide-dark" || p.size === "wide-light" ? "md:col-span-12" :
                "md:col-span-6";
              const dark = p.size === "wide-dark";
              const wide = p.size === "wide-dark" || p.size === "wide-light";

              if (wide) {
                return (
                  <article
                    key={p.id}
                    data-testid={`press-${p.slug}`}
                    className={`${span} rounded-3xl overflow-hidden border ${dark ? "bg-[#2D2623] text-[#FDFBF7] border-[#FDFBF7]/10" : "bg-[#F4EFE6] text-[#2D2623] border-[#A39171]/15"} group relative`}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-12">
                      <div className={`md:col-span-7 aspect-[16/10] md:aspect-auto overflow-hidden ${dark ? "bg-[#2D2623]" : "bg-[#FDFBF7]"}`}>
                        <img src={p.image_url} alt={p.title} className="w-full h-full object-cover" />
                      </div>
                      <div className="md:col-span-5 p-8 md:p-12 relative flex flex-col justify-center">
                        {dark && <div className="absolute inset-0 grain opacity-30 pointer-events-none" />}
                        <p className={`eyebrow mb-3 relative ${dark ? "text-[#E2C044]" : "text-[#A39171]"}`}>{p.eyebrow}</p>
                        <h3 className="font-display text-3xl md:text-4xl tracking-tight leading-tight relative">
                          {p.title}
                        </h3>
                        <p className={`mt-5 leading-relaxed relative ${dark ? "text-[#FDFBF7]/75" : "text-[#5C524E]"}`}>
                          {p.description}
                        </p>
                      </div>
                    </div>
                  </article>
                );
              }

              const tall = p.size === "md";
              return (
                <article
                  key={p.id}
                  data-testid={`press-${p.slug}`}
                  className={`${span} bg-[#F4EFE6] rounded-3xl overflow-hidden border border-[#A39171]/15 hover:shadow-[0_24px_60px_rgba(163,145,113,0.15)] transition-shadow`}
                >
                  <div className={`${tall ? "aspect-[4/5]" : "aspect-[16/10]"} overflow-hidden bg-white`}>
                    <img src={p.image_url} alt={p.title} className="w-full h-full object-contain" />
                  </div>
                  <div className="p-7 md:p-8">
                    <p className="eyebrow text-[#A39171] mb-2">{p.eyebrow}</p>
                    <h3 className={`font-display ${tall ? "text-2xl" : "text-2xl md:text-3xl"} text-[#2D2623] tracking-tight leading-snug`}>
                      {p.title}
                    </h3>
                    <p className={`text-[#5C524E] mt-3 ${tall ? "text-sm" : ""} leading-relaxed`}>
                      {p.description}
                    </p>
                  </div>
                </article>
              );
            })}
            {press.length === 0 && (
              <p className="md:col-span-12 text-[#A39171]">{t("Loading press features…")}</p>
            )}
          </div>

          <p className="mt-10 text-sm text-[#A39171] max-w-3xl">
            {t("Both doctors have appeared on TV channels, in newspapers, magazines, and at medical and non-medical organisations — speaking on psychiatry, addiction, sexology and neuromodulation. Available for further interviews on request.")}
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
          <h2 className="font-display text-4xl md:text-5xl text-[#2D2623] tracking-tight leading-tight">
            {t("\"Healing happens at the speed of")} <span className="italic text-[#E07A5F]">{t("trust")}</span>."
          </h2>
          <p className="mt-6 text-lg text-[#5C524E] max-w-2xl mx-auto leading-relaxed">
            {t("Whether you are reaching out for yourself or for someone you love, our team will receive you with the discretion, care and clinical rigour you deserve.")}
          </p>
          <Link
            to="/contact"
            data-testid="about-contact-cta"
            className="mt-10 inline-flex items-center gap-2 px-7 h-12 rounded-full bg-[#2D2623] hover:bg-[#E07A5F] text-[#FDFBF7] transition-all font-medium"
          >
            {t("Speak with our team")} <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
